import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Icon } from "@astryxdesign/core/Icon";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { HStack, StackItem } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { useStudio } from "@/features/studio/StudioRoot";
import { publicApi } from "@/lib/api/public";
import { errorMessage, toAppError } from "@/lib/errors";
import { formatDateTime, formatDuration, formatPrice, localDateOf } from "@/lib/format";
import { contactDraft, myBookings, pendingKeys } from "@/lib/storage";
import { ServiceList } from "@/features/catalog/ServiceList";
import { useBookingNav, type BookingStep } from "./useBookingNav";
import { TimeStep } from "./TimeStep";
import { ContactsStep, type Contacts } from "./ContactsStep";
import { useT } from "@/lib/i18n";

const STEP_ORDER: BookingStep[] = ["service", "time", "contacts", "review"];

export function BookingSheet() {
  const studio = useStudio();
  const t = useT();
  const liveNav = useBookingNav();
  // While the sheet animates closed the URL is already clean; keep showing the
  // last open step instead of flashing the first one.
  const lastOpen = useRef(liveNav);
  if (liveNav.isOpen) lastOpen.current = liveNav;
  const nav = liveNav.isOpen ? liveNav : { ...lastOpen.current, isOpen: false, go: liveNav.go, close: liveNav.close, open: liveNav.open };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const service = studio.services.find((s) => s.id === nav.serviceId) ?? null;
  const [contacts, setContacts] = useState<Contacts>(() => ({ ...contactDraft.get(studio.slug), comment: "" }));
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Steps that need earlier data fall back to the first missing step (deep link, reload).
  let step = nav.step;
  if (!service && step !== "service") step = "service";
  else if (!nav.at && (step === "contacts" || step === "review")) step = "time";
  else if (step === "review" && !contacts.name) step = "contacts";

  useEffect(() => {
    if (nav.isOpen) titleRef.current?.focus({ preventScroll: true });
  }, [step, nav.isOpen]);

  const fingerprint = `${nav.serviceId}|${nav.at}|${contacts.name}|${contacts.phone}|${contacts.email}|${contacts.comment}`;
  const create = useMutation({
    mutationFn: () =>
      publicApi.createBooking({
        slug: studio.slug,
        serviceId: service!.id,
        startsAt: nav.at!,
        name: contacts.name,
        phone: contacts.phone,
        email: contacts.email,
        comment: contacts.comment,
        // Same draft → same key: a retry after a lost response returns the same booking and link.
        idempotencyKey: pendingKeys.get(`book:${studio.slug}`, fingerprint),
      }),
    onSuccess: (res) => {
      pendingKeys.clear(`book:${studio.slug}`, fingerprint);
      myBookings.save(studio.slug, {
        token: res.access_token,
        startsAt: res.booking.starts_at,
        serviceName: res.booking.service_name,
        savedAt: new Date().toISOString(),
      });
      void queryClient.invalidateQueries({ queryKey: ["slots", studio.slug] });
      queryClient.setQueryData(["booking", res.access_token], res.booking);
      navigate(`/s/${studio.slug}/b#t=${res.access_token}`, { replace: true, state: { justBooked: true } });
    },
    onError: (e) => {
      if (toAppError(e).code === "slot_unavailable") void queryClient.invalidateQueries({ queryKey: ["slots", studio.slug] });
    },
  });
  const error = create.error ? toAppError(create.error) : null;

  const stepIndex = STEP_ORDER.indexOf(step) + (nav.serviceId ? 0 : 1);
  const totalSteps = nav.serviceId && nav.step !== "service" ? 3 : 4;

  return (
    <Drawer
      open={nav.isOpen}
      onOpenChange={(open) => {
        if (!open) nav.close();
      }}
      showSwipeHandle
    >
      <DrawerContent className="sheet-content" aria-describedby="booking-step-desc">
        <DrawerPrimitive.VirtualKeyboardProvider>
          <VStack gap={4} paddingInline={4} paddingBlockStart={2} className="sheet-footer overflow-y-auto overscroll-contain">
            <HStack gap={2} vAlign="center">
              <StackItem size="fill">
                <DrawerTitle ref={titleRef} tabIndex={-1} className="outline-none text-start text-lg font-semibold">
                  {t.booking.steps[step]}
                </DrawerTitle>
                <DrawerDescription id="booking-step-desc" className="text-start">
                  {t.booking.stepOf(Math.min(stepIndex, totalSteps), totalSteps)}
                  {service ? ` · ${service.name}` : ""}
                </DrawerDescription>
              </StackItem>
              <IconButton label={t.booking.closeLabel} variant="ghost" icon={<Icon icon="close" />} onClick={nav.close} />
            </HStack>

            {step === "service" ? (
              <ServiceList studio={studio} services={studio.services} onPick={(s) => nav.go({ book: s.id, step: "time" })} />
            ) : null}

            {step === "time" && service ? (
              <TimeStep
                studio={studio}
                service={service}
                date={nav.date}
                onDate={(d) => nav.go({ date: d }, { replace: true })}
                onPick={(slot) => nav.go({ at: slot.starts_at, date: slot.local_date, step: "contacts" })}
              />
            ) : null}

            {step === "contacts" ? (
              <ContactsStep
                initial={contacts}
                onSubmit={(c) => {
                  setContacts(c);
                  contactDraft.set(studio.slug, { name: c.name, phone: c.phone, email: c.email });
                  nav.go({ step: "review" });
                }}
              />
            ) : null}

            {step === "review" && service && nav.at ? (
              <VStack gap={4}>
                <MetadataList>
                  <MetadataListItem label={t.booking.service}>{service.name}</MetadataListItem>
                  <MetadataListItem label={t.booking.when}>{formatDateTime(nav.at, studio.timezone)}</MetadataListItem>
                  <MetadataListItem label={t.booking.duration}>{formatDuration(service.duration_minutes)}</MetadataListItem>
                  <MetadataListItem label={t.booking.price}>{formatPrice(service, studio.currency)}</MetadataListItem>
                  <MetadataListItem label={t.booking.contacts}>{`${contacts.name}, ${contacts.phone}`}</MetadataListItem>
                </MetadataList>
                {studio.config.policies.cancellation ? <Text type="supporting">{studio.config.policies.cancellation}</Text> : null}
                {studio.config.policies.prepayment ? <Text type="supporting">{studio.config.policies.prepayment}</Text> : null}
                {error ? (
                  <Banner
                    status="error"
                    title={error.code === "slot_unavailable" ? t.booking.slotTakenTitle : t.booking.notCreated}
                    description={errorMessage(error)}
                    collapsible={false}
                    endContent={
                      error.code === "slot_unavailable" || error.code === "slot_not_offered" ? (
                        <Button label={t.booking.otherTime} size="sm" onClick={() => nav.go({ step: "time", at: null, date: localDateOf(nav.at!, studio.timezone) })} />
                      ) : undefined
                    }
                  />
                ) : null}
                <Button
                  label={create.isPending ? t.booking.sending : error && error.code === "network" ? t.booking.resend : t.booking.submit}
                  variant="primary"
                  size="lg"
                  width="100%"
                  isLoading={create.isPending}
                  onClick={() => create.mutate()}
                />
                <Button label={t.booking.editContacts} variant="ghost" width="100%" onClick={() => nav.go({ step: "contacts" })} />
              </VStack>
            ) : null}
          </VStack>
        </DrawerPrimitive.VirtualKeyboardProvider>
      </DrawerContent>
    </Drawer>
  );
}
